import { useState, useEffect, useLayoutEffect } from 'react';
import { authPostRequest, getAuthRequest, putRequest } from 'Api/api';
import SuccessError from 'components/SuccessError';
import { useSelector } from 'react-redux';
import StoreForm from 'components/StoreForm';
import ModalComp from 'components/Modal';
import PropTypes from 'prop-types';
import Loader from 'components/Loader';
import CenteredWrapper from 'pages/updatePassword/CenteredWrapper';
import { ValidateStoreFields } from 'pages/storePages/ValidateStoreFields';
import { v4 as uuidV4 } from 'uuid';
import FileResizer from 'react-image-file-resizer';
import { PLACEHOLDER_IMG_SRC } from '../../constant/constants';

const initialStoreState = {
  storeName: '',
  contactName: '',
  email: '',
  phone1: '',
  phone2: '',
  address: '',
  markupPercentage: '',
  storeLogoURL: ''
};

const tiers = [
  {
    label: 'TIER2 (25%)',
    value: 25
  },
  {
    label: 'TIER3 (35%)',
    value: 35
  },
  {
    label: 'TIER4 (45%)',
    value: 45
  },
  {
    label: 'TIER5 (0%)',
    value: 0
  }
];

const CreateAndUpdateStore = ({
  store,
  addOpenModal,
  setAddOpenModal,
  setSuccess,
  setAlertBar,
  setIsFormSaved,
  isFormSaved
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);

  // state will be empty in case of add object
  const [storeInfo, setStoreInfo] = useState({
    storeName: store.id ? store?.storeName : '',
    contactName: store.id ? store?.contactName : '',
    email: store.id ? store?.email : '',
    phone1: store.id ? store?.phone1 : '',
    phone2: store.id ? store?.phone2 : '',
    address: store.id ? store?.address : '',
    storeLogoURL: store.id ? store?.storeLogoURL : null,
    basePriceTypes: store.id ? store?.basePriceTypes : [],
    currentBasePriceTypeId: store.id ? store?.currentBasePriceTypeId : 0
  });

  const [tier, setTier] = useState({
    label: store.id ? store?.currentTier : 'TIER2',
    value: store.id ? store?.currentTierPercentage : 25
  });

  const [tiersLocal, setTierLocal] = useState(tiers);
  const [successMessage, setSuccessMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSaved, setIsSaved] = useState(false);

  const { userData } = useSelector((store) => store.auth);

  useEffect(() => {
    if (store.storeName.length) {
      const tiersFromApi = [
        {
          value: store.tier5Percentage,
          label: `TIER5 (${store.tier5Percentage}%)`
        },
        {
          value: store.tier4Percentage,
          label: `TIER4 (${store.tier4Percentage}%)`
        },
        {
          value: store.tier3Percentage,
          label: `TIER3 (${store.tier3Percentage}%)`
        },
        {
          value: store.tier2Percentage,
          label: `TIER2 (${store.tier2Percentage}%)`
        }
      ];
      setTierLocal(tiersFromApi);
    } else {
      setTierLocal(tiers);
    }
  }, [store]);

  useLayoutEffect(() => {
    getBasePriceTypes();
  }, [store?.currentBasePriceTypeId]);

  const getBasePriceTypes = async () => {
    if (!store?.currentBasePriceTypeId) {
      try {
        const basePriceTypes = await getAuthRequest('getBasePriceTypes');
        const basePriceTypesOptions = basePriceTypes?.map((type) => ({
          basePriceType: type?.name,
          basePriceTypeId: type?.id
        }));
        setStoreInfo((prevState) => ({
          ...prevState,
          basePriceTypes: basePriceTypesOptions,
          currentBasePriceTypeId: basePriceTypesOptions?.filter(
            (type) => type.basePriceType === 'Wholesale Price'
          )[0]?.basePriceTypeId
        }));
      } catch (error) {
        console.error('Error fetching base price types:', error);
      }
    }
  };

  //Updates the store information state with the value from an input field.
  const handleStoreInfo = (e) => {
    if (e.target.name == 'storeLogoURL') {
      setLogoFile(e.currentTarget.files[0]);
    }
    setStoreInfo({ ...storeInfo, [e.target.name]: e.target.value });
    handleError(e.target.name, null);
  };

  //Updates the tier state with the selected tier value from a dropdown.
  const handleTierChange = (event) => {
    const value = event.target.value;
    setTier({
      value: value,
      label: tiersLocal?.find((tier) => tier.value === value)?.label?.slice(0, 5)
    });
  };

  const handleBasePriceTypeChange = (event) => {
    const value = event.target.value;
    setStoreInfo({ ...storeInfo, currentBasePriceTypeId: value });
  };

  const handleError = (input, errorMessage) => {
    setErrors((prevState) => ({ ...prevState, [input]: errorMessage }));
  };

  //save and add more
  const saveAndAddMore = async (e) => {
    e.preventDefault();
    setSuccess([]);
    setErrorMessage([]);

    console.log('event', e, e.target);
    const createStore = await handleSubmit();
    if (createStore?.resStatus === 1) {
      setIsFormSaved(!isFormSaved);
      setSuccessMessage([createStore?.resMessage]);
      setIsSaved((prevState) => !prevState);

      setStoreInfo(initialStoreState);
      await getBasePriceTypes();
    }

    if (createStore?.resStatus === 0) {
      setErrorMessage([createStore?.resMessage]);
    }
  };

  //Save and close
  const saveAndClose = async (e) => {
    e.preventDefault();
    setErrorMessage([]);
    setSuccess([]);
    setAlertBar(false);
    const createStore = await handleSubmit();

    if (createStore?.resStatus === 1) {
      setIsFormSaved(!isFormSaved);
      setSuccess([createStore?.resMessage]);

      setStoreInfo(initialStoreState);
      setAddOpenModal(false);
    }

    if (createStore?.resStatus === 0) {
      setErrorMessage([createStore?.resMessage]);
    }

    setAlertBar(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setErrorMessage([]);
    setSuccessMessage([]);
    // if there is not logo info in store state or if URL is off place holder img then
    // prompt user to add a store logo
    if (!storeInfo?.storeLogoURL || storeInfo?.storeLogoURL == PLACEHOLDER_IMG_SRC) {
      handleError('storeLogoURL', 'Please add a store logo!');
    }

    const isFormValid =
      ValidateStoreFields(storeInfo, handleError) &&
      !errors?.storeLogoURL && //no store logo URL errors present
      storeInfo?.storeLogoURL && // store logo URL is present
      storeInfo?.storeLogoURL != PLACEHOLDER_IMG_SRC; // store logo URL is not of place holder img
    if (!isFormValid) return;

    // unique name
    const fileName = uuidV4();
    // url to request backend to get upload url for s3

    try {
      let requestParams = {
        userId: userData.userId,
        storeId: store?.id ? store?.id : '', // will be empty in case of add
        storeName: storeInfo.storeName.trim(),
        contactName: storeInfo.contactName.trim(),
        basePriceTypeId: storeInfo.currentBasePriceTypeId,
        email: storeInfo.email.trim(),
        phone1: storeInfo.phone1.trim(),
        phone2: storeInfo.phone2.trim(),
        address: storeInfo.address.trim(),
        currentTierPercentage: tier.value,
        currentTier: tier.label,
        storeLogoURL: storeInfo?.storeLogoURL
      };

      setShowLoader(true);
      setShowAlertBar(false);

      if (logoFile) {
        const fileExtension = logoFile?.name?.split('.').pop();
        const url = 'generate-upload-url?path=' + 'stores-logos/' + fileName + '.' + fileExtension;

        const s3UploadURL = await getAuthRequest(url);
        let resizedFileOutput = logoFile;
        resizedFileOutput = await new Promise((resolve) => {
          FileResizer.imageFileResizer(
            logoFile,
            250,
            250,
            'webp',
            90,
            0,
            (resizedFile) => {
              resolve(resizedFile);
            },
            'file'
          );
        });

        await putRequest(s3UploadURL, resizedFileOutput, {
          headers: {
            'Content-Type': 'image/jpeg'
          }
        });

        requestParams = {
          ...requestParams,
          storeLogoURL: 'stores-logos/' + fileName + '.' + fileExtension
        };
      }

      const createStore = isFormValid && (await authPostRequest('saveStore', requestParams));

      setShowLoader(false);
      setShowAlertBar(true);
      return createStore;
    } catch (err) {
      setErrorMessage([...errorMessage, 'Error']);
      window.scrollTo(0, 0);
    }
  };

  return (
    <CenteredWrapper>
      <ModalComp
        modalWidth={600}
        open={addOpenModal}
        setOpen={setAddOpenModal}
        header={store?.id ? 'Update Store' : 'Create Store'}>
        {showAlertBar && (
          <SuccessError
            successMessage={successMessage}
            errorMessage={errorMessage}
            setShowAlertBar={setShowAlertBar}
          />
        )}

        {showLoader && <Loader open={showLoader} />}

        <StoreForm
          handleSubmit={saveAndAddMore}
          buttonText="Save & Add More"
          buttonClose="Save & Close"
          handleError={handleError}
          handleCancelAction={saveAndClose}
          handleStoreInfo={handleStoreInfo}
          storeInfo={storeInfo}
          error={errors}
          store={store}
          tiers={tiersLocal}
          handleTierChange={handleTierChange}
          handleBasePriceTypeChange={handleBasePriceTypeChange}
          value={tier?.value}
          isSaved={isSaved}
        />
      </ModalComp>
    </CenteredWrapper>
  );
};

CreateAndUpdateStore.propTypes = {
  store: PropTypes.object,
  addOpenModal: PropTypes.bool,
  setAddOpenModal: PropTypes.func,
  isFormSaved: PropTypes.bool,
  setIsFormSaved: PropTypes.func,
  setSuccess: PropTypes.func,
  setAlertBar: PropTypes.func
};

export default CreateAndUpdateStore;
